import { accentHexForFormato } from '@/constants/obras-generos';

export const key_encrypt_login = "mediocre-admin";

export const imageUrl: string = "https://cdn.mediocrescan.com/";
export const uploadImageUrl: string = "https://upload.mediocretoons.site/upload-imagens";

// Formatação de dinheiro
export const formatMoney = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
        .format(value / 100)
        .replace('R$', '');
};

export const formatMoneyBase = (numero: string): string => {
    if (!numero) return '';
    if (parseFloat(numero) === 0) return '';

    numero = numero.replace('.', '');

    if ((numero && /^\d{2,}$/.test(numero)) || numero.length < 3) {
        const parteDecimal = numero.length === 1 ? `0${numero}` : numero.slice(-2);
        const parteInteira = numero.slice(0, -2);
        let numeroFormatado = parseFloat(`${parteInteira}.${parteDecimal}`)
            .toFixed(2)
            .replace('.', ',');
        numeroFormatado = numeroFormatado.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return numeroFormatado || '';
    } else {
        return '';
    }
};

export const formatNumberBase = (numero: number | string): string => {
    // Garantir que numero não seja null/undefined antes de converter e transformar
    if (numero === null || numero === undefined) return '';
    const numStr = numero.toString();
    return numStr ? numStr.replace(/\B(?=(\d{3})+(?!\d))/g, '.') : '';
};

// Formatação de data
const meses = [
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"
];

export function dataAtualFormatada(): string {
    const data = new Date();
    const dia = data.getDate();
    const mes = meses[data.getMonth()];
    const ano = data.getFullYear();
    return `${dia} ${mes} de ${ano}`;
}

// Redimensionar imagem
export const resizeImage = (file: string): Promise<string> => {
    if (typeof document === 'undefined') return Promise.resolve(file); // SSR safety
    return new Promise((resolve) => {
        const img = new Image();
        img.src = file;
        img.onload = () => {
            const canvas = document?.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const originalWidth = img.width;
            const originalHeight = img.height;

            let width = originalWidth;
            let height = originalHeight;

            // Unused vars kept for structure consistency with original if needed in future logic or limits
            // const maxWidth = 2048;
            // const maxHeight = 1024;

            canvas.width = width;
            canvas.height = height;

            ctx?.drawImage(img, 0, 0, width, height);

            resolve(canvas.toDataURL('image/webp', 0.8)); // Qualidade 80%
        };
    });
};

// Gerar cor aleatória RGBA
export function gerarCorAleatoriaRGBA(): string {
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);

    return `rgba(${r}, ${g}, ${b}, 1)`;
}

// Remover acentos
export function removeAcentos(texto: string): string {
    return texto
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9\s]/g, "")
        .toLowerCase();
}

export async function uploadImage(file: File, pasta: string): Promise<string | null> {
    let objectUrl: string | null = null;
    try {
        if (typeof document === 'undefined') {
            throw new Error('Upload de imagem não suportado server-side');
        }

        const processedFile = await new Promise<Blob>((resolve, reject) => {
            const img = new Image();
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx?.drawImage(img, 0, 0);
                canvas.toBlob((blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('Erro ao processar imagem'));
                    }
                }, 'image/webp', 0.8);
            };

            img.onerror = () => reject(new Error('Erro ao carregar imagem'));
            objectUrl = URL.createObjectURL(file);
            img.src = objectUrl;
        });

        // Limpar URL do objeto
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
        }

        // Criar FormData
        const formData = new FormData();
        formData.append('file', processedFile);
        formData.append('pasta', pasta);
        formData.append('tipo', 'webp');

        // Fazer upload
        const uploadResponse = await fetch(uploadImageUrl, {
            method: 'POST',
            body: formData,
        });

        if (!uploadResponse.ok) {
            const errorData = await uploadResponse.json().catch(() => ({}));
            throw new Error(errorData.error || 'Erro ao fazer upload da imagem');
        }

        const uploadData = await uploadResponse.json();

        if (!uploadData.nomes || !Array.isArray(uploadData.nomes) || uploadData.nomes.length === 0) {
            throw new Error('Resposta inválida do servidor de upload');
        }

        return uploadData.nomes[0];
    } catch (error) {
        // Limpar URL do objeto em caso de erro
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
        }
        console.error('Erro ao fazer upload:', error);
        throw error;
    }
}

// Ranking System
export const calculateProgress = (totalXp: number, currentLevel: number): number => {
    // XP necessário para alcançar o nível atual (inicio da faixa)
    // Ajuste para Nível 1 começar em 0
    const xpStart = currentLevel <= 1 ? 0 : Math.floor(50 * Math.pow(currentLevel, 1.6));

    // XP necessário para alcançar o próximo nível (fim da faixa)
    const xpEnd = Math.floor(50 * Math.pow(currentLevel + 1, 1.6));

    if (xpEnd <= xpStart) return 100;

    const progress = ((totalXp - xpStart) / (xpEnd - xpStart)) * 100;
    return Math.min(100, Math.max(0, progress));
};
// Link generators
export function generateWorkLink(obraId: number | string): string {
    return `/obra/${obraId}`;
}

export function generateChapterLink(chapterId: number | string): string {
    return `/capitulo/${chapterId}`;
}

/** Cor de acento por `formato` — mesma tabela que a home do app (`OBRAS_GENEROS`). */
export function getFormatoColor(formatoId?: number): string {
    return accentHexForFormato(formatoId ?? 5);
}
